import * as React from 'react';
import { useAuthContext } from './auth-provider.js';
import { useAsyncAction } from './use-async-action.js';
/**
 * The action hooks below all follow the same shape: a stable `run(...)` plus
 * `loading` / `error`. Components are expected to await `run` for the result
 * and render the inline error message themselves if they want one.
 *
 * They deliberately don't call into the AuthProvider's `setUser` themselves —
 * the API client already persists the token, and `useAuthMe` reloads from
 * `/auth/me` on the next render that needs identity. This keeps each hook
 * focused on a single endpoint.
 */
export function useLogin() {
    const { client, setUser } = useAuthContext();
    const action = useAsyncAction(async (input) => {
        const outcome = await client.login(input);
        if (outcome.status === 'authenticated')
            setUser(outcome.result.session);
        return outcome;
    });
    return action;
}
export function useSignup() {
    const { client, setUser } = useAuthContext();
    return useAsyncAction(async (input) => {
        const result = await client.signup(input);
        if (result.result !== undefined)
            setUser(result.result.session);
        return result;
    });
}
/**
 * Ends the session. **This device by default** (`USR-007` AC-1/AC-3); pass `'everywhere'` for
 * the explicit lost-phone act (`ADR 051` §4).
 *
 * The default is the whole point of the parameter having one: until 2026-09-03 there was no
 * choice and the behaviour was everywhere, so a member signing out of a borrowed laptop also
 * signed out the phone in their pocket.
 */
export function useLogout() {
    const { client, setUser } = useAuthContext();
    return useAsyncAction(async (scope = 'device') => {
        if (scope === 'everywhere')
            await client.logoutEverywhere();
        else
            await client.logout();
        setUser(null);
    });
}
export function useOtpLogin() {
    const { client, setUser } = useAuthContext();
    const initiate = useAsyncAction(async (input) => {
        return client.initiateOtpLogin(input);
    });
    const complete = useAsyncAction(async (input) => {
        const outcome = await client.completeOtpLogin(input);
        if (outcome.status === 'authenticated')
            setUser(outcome.result.session);
        return outcome;
    });
    return { initiate, complete };
}
export function useMfaComplete() {
    const { client, setUser } = useAuthContext();
    return useAsyncAction(async (input) => {
        const result = await client.completeMfa(input);
        setUser(result.session);
        return result;
    });
}
/**
 * Self-service authenticator enrolment, all three legs on the caller's own session. `begin`
 * returns the shared secret + otpauth URL; `complete` submits the first code from the app, after
 * which every later login is challenged; `disable` turns it back off.
 *
 * `disable` is here rather than in `useMfaSettings` because it is the same factor as the other
 * two — the account's authenticator — while `/auth/me/mfa` carries the unrelated email-code knob.
 * A caller that renders enrolment state needs `useMfaSettings().settings.totpEnabled`, which
 * reads the directory rather than assuming this session did the enrolling.
 */
export function useTotpEnrolment() {
    const { client } = useAuthContext();
    const begin = useAsyncAction(async () => client.beginTotpEnrolment());
    const complete = useAsyncAction(async (input) => {
        await client.completeTotpEnrolment(input);
    });
    const disable = useAsyncAction(async () => {
        await client.disableTotp();
    });
    return { begin, complete, disable };
}
export function usePasswordRecovery() {
    const { client, setUser } = useAuthContext();
    const initiate = useAsyncAction(async (input) => {
        return client.initiatePasswordRecovery(input);
    });
    const complete = useAsyncAction(async (input) => {
        const result = await client.completePasswordRecovery(input);
        setUser(result.session);
        return result;
    });
    return { initiate, complete };
}
/**
 * The mailed reset link for an unverified address (`USR-004` AC-8): inspect it, then spend it.
 * Neither touches the session — completing one ends every session the account had, and the
 * holder signs in afresh.
 */
export function usePasswordResetLink() {
    const { client } = useAuthContext();
    const inspect = useAsyncAction(async (input) => client.inspectPasswordResetLink(input));
    const complete = useAsyncAction(async (input) => client.completePasswordResetLink(input));
    return { inspect, complete };
}
export function useChangePassword() {
    const { client } = useAuthContext();
    return useAsyncAction(async (input) => {
        await client.changePassword(input);
    });
}
/**
 * Loads the list of social identity providers the API advertises (`/auth/social/providers`).
 * Returns `{ providers, loading, error }` — `providers` is a stable empty array until the
 * fetch resolves so consumers can render unconditionally without flicker. The list is fetched
 * once per provider/client pair; mount the hook on the login page.
 */
export function useSocialProviders() {
    const { client } = useAuthContext();
    const [providers, setProviders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        client
            .listSocialProviders()
            .then((next) => {
            if (cancelled)
                return;
            // Anything but a list is no providers. A screen that draws social sign-in only when there
            // is some (TW 44131761) must not be broken by an answer it cannot iterate.
            setProviders(Array.isArray(next) ? next : []);
            setError(null);
        })
            .catch((reason) => {
            if (cancelled)
                return;
            setError(reason instanceof Error ? reason : new Error(String(reason)));
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [client]);
    return { providers, loading, error };
}
/**
 * Completes a social-login round-trip after the OAuth callback. Pass the `initCode` the SPA
 * stored before navigating to the IdP and the `returnToCode` Kratos appended to the return
 * URL; the hook trades them for a session and persists the resulting bearer token via the
 * normal login path.
 */
export function useSocialExchange() {
    const { client, setUser } = useAuthContext();
    return useAsyncAction(async (input) => {
        const result = await client.exchangeSocialLogin(input);
        setUser(result.session);
        return result;
    });
}
/**
 * Action bundle for the link-credentials surface. Each action persists the resulting bearer
 * token on success via `setUser`, mirroring how `useLogin` does it — to the rest of the SPA
 * the link-completion looks identical to a normal login.
 */
export function useLinkAccount() {
    const { client, setUser } = useAuthContext();
    const submitPassword = useAsyncAction(async (input) => {
        const result = await client.submitLinkPassword(input);
        setUser(result.session);
        return result;
    });
    const initiateCode = useAsyncAction(async (input) => {
        await client.initiateLinkCode(input);
    });
    const completeCode = useAsyncAction(async (input) => {
        const result = await client.completeLinkCode(input);
        setUser(result.session);
        return result;
    });
    return { submitPassword, initiateCode, completeCode };
}
export function useEmailVerification() {
    const { client } = useAuthContext();
    const initiate = useAsyncAction(async (input) => {
        return client.initiateEmailVerification(input);
    });
    const complete = useAsyncAction(async (input) => {
        await client.completeEmailVerification(input);
    });
    return { initiate, complete };
}
export function useUpdateProfile() {
    const { client, setUser } = useAuthContext();
    return useAsyncAction(async (input) => {
        const next = await client.updateProfile(input);
        setUser(next);
        return next;
    });
}
export function useMfaSettings() {
    const { client } = useAuthContext();
    const [settings, setSettings] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const next = await client.getMfaSettings();
            setSettings(next);
        }
        finally {
            setLoading(false);
        }
    }, [client]);
    React.useEffect(() => {
        void load();
    }, [load]);
    const update = useAsyncAction(async (input) => {
        await client.updateMfaSettings(input);
        // Server returns no body — reflect the change locally so the UI updates
        // without a second round-trip. Only the email-code knob moved; the authenticator is a
        // separate factor and this endpoint does not touch it.
        setSettings((current) => ({
            totpEnabled: current?.totpEnabled ?? false,
            emailCodeEnabled: input.emailCodeEnabled,
        }));
    });
    return { settings, loading, reload: load, update };
}
/**
 * Bundle of admin user management operations. Returns one stable object so
 * pages can destructure exactly the methods they need without each method
 * spinning its own context lookup.
 */
export function useAdminUsers() {
    const { client } = useAuthContext();
    const list = useAsyncAction(async (options) => {
        return client.adminListUsers(options);
    });
    const get = useAsyncAction(async (id) => client.adminGetUser(id));
    const create = useAsyncAction(async (input) => client.adminCreateUser(input));
    const update = useAsyncAction(async (vars) => client.adminUpdateUser(vars.id, vars.input));
    const remove = useAsyncAction(async (id) => client.adminDeleteUser(id));
    const setGroups = useAsyncAction(async (vars) => client.adminSetUserGroups(vars.id, vars.groupNames));
    const addGroup = useAsyncAction(async (vars) => client.adminAddUserGroup(vars.id, vars.group));
    const removeGroup = useAsyncAction(async (vars) => client.adminRemoveUserGroup(vars.id, vars.group));
    const resetPassword = useAsyncAction(async (id) => client.adminResetUserPassword(id));
    return { list, get, create, update, remove, setGroups, addGroup, removeGroup, resetPassword };
}
//# sourceMappingURL=auth-actions.js.map